import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { definirTipoPagoCobro, getCobroPorId, getCobrosPorCliente } from '../../services/cobranzaService';
import { vincularCobroTurno, cambiarEstado } from '../../services/turneroService';
import { ChipEstadoCobro } from '../../components/Cobranzas/estadoCobro';
import { formatoMoneda } from '../../components/Cobranzas/PanelesCobro';
import { PAGO_TODO, esPagoTodo } from '../../components/Cobranzas/resumenCobro';
import DetalleCobranza from '../../components/Cobranzas/DetalleCobranza';
import ModalRegistrarPago from '../../components/Cobranzas/ModalRegistrarPago';
import ModalNuevoCobro from '../../components/Cobranzas/ModalNuevoCobro';
import HistorialTurnosCliente from '../../components/recepcion/HistorialTurnosCliente';
import { LEYENDA_CRITERIO } from '../../components/recepcion/VincularClienteTurno';
import { apellidoNombre } from '../../utils/nombrePersona';
import { metaTramite, rutaDetalleTramite } from '../../utils/rutasTramite';
import { CampoDato, SelectorCatalogoConAlta, formatearFecha, formatearHorario, formatearTipoTramite } from './atencionUi';
import '../../style/components/TramitesCliente.css';
import '../../style/pages/VistaDetalladaAtencion.css';

/**
 * Atención de un turno de cobro.
 *
 * POR QUÉ ES UNA PANTALLA APARTE
 * Un turno de cobro no se atiende como el mostrador: no abre trámites, no lleva checklist de
 * documentación y no tiene motivo de no avance (no se espera que avance nada — la persona vino a
 * pagar). Mismo criterio que el flujo de ANSES, que también tiene etapa propia.
 *
 * QUÉ COBRO SE MUESTRA — Y POR QUÉ ES ESTADO LOCAL
 * `idCobroSeleccionado` es un useState, no `turno.idCobro`. Antes el cobro mostrado salía del
 * turno, así que "elegir otro cobro" tenía que ir al backend a limpiar la columna y esperar la
 * respuesta: mientras tanto se caía la condición que renderiza esta ficha y la pantalla entera se
 * reemplazaba por el selector — se leía como si te hubiera sacado a otro lado. Ahora el render
 * depende solo del estado local: cambiar de cobro es instantáneo y nunca desmonta la ficha.
 *
 * El vínculo se sigue persistiendo (recepcion_turno.id_cobro, V228) pero como efecto secundario:
 * si el PATCH falla se avisa y la pantalla no se mueve. Esto además saca del camino el redirect
 * duro a /login que hace apiClient ante un 401, que antes se disparaba en cada cambio de cobro.
 *
 * El saldo NUNCA se calcula acá: llega calculado del backend (monto_total − pagos).
 */
export default function VistaDetalladaAtencionCobro({
    turno,
    estadoActual,
    clienteAtencion,
    tramitesCliente,
    notas,
    setNotas,
    resultadoAtencion,
    setResultadoAtencion,
    resultadosAtencion,
    guardando,
    setGuardando,
    onEmpezarAtencion,
    onError,
    onTurnoActualizado,
    onAbrirVinculoCliente,
    onAbrirTramite,
    onAtencionFinalizada,
}) {
    const [cobros, setCobros] = useState([]);
    const [cobroDetalle, setCobroDetalle] = useState(null);
    const [cargandoLista, setCargandoLista] = useState(false);
    const [cargandoDetalle, setCargandoDetalle] = useState(false);
    const [idCobroSeleccionado, setIdCobroSeleccionado] = useState(turno?.idCobro || null);
    const [modalPagoAbierto, setModalPagoAbierto] = useState(false);
    const [modalNuevoAbierto, setModalNuevoAbierto] = useState(false);

    const cuil = clienteAtencion?.cuil || turno?.cuilCliente || null;
    const soloLectura = estadoActual === 'FINALIZADO';
    const esEtapaUno = estadoActual === 'ASISTIO';

    const cargarLista = useCallback(async () => {
        if (!cuil) {
            setCobros([]);
            return [];
        }
        setCargandoLista(true);
        try {
            const data = await getCobrosPorCliente(cuil);
            const lista = Array.isArray(data) ? data : [];
            setCobros(lista);
            return lista;
        } catch (e) {
            setCobros([]);
            return [];
        } finally {
            setCargandoLista(false);
        }
    }, [cuil]);

    // El detalle se pide aparte porque /por-cliente devuelve el resumen (CobroListadoDto), sin
    // cuotas/pagos/gestiones: el detalle necesita el CobroDetalleDto de GET /cobros/{id}.
    // Es el callback de refresco que reciben DetalleCobranza y los modales.
    const cargarDetalle = useCallback(async () => {
        if (!idCobroSeleccionado) {
            setCobroDetalle(null);
            return;
        }
        setCargandoDetalle(true);
        try {
            setCobroDetalle(await getCobroPorId(idCobroSeleccionado));
        } catch (e) {
            setCobroDetalle(null);
        } finally {
            setCargandoDetalle(false);
        }
    }, [idCobroSeleccionado]);

    /** Cambia el cobro mostrado ya mismo y recién después intenta dejarlo registrado. */
    const elegirCobro = useCallback((idCobro) => {
        setIdCobroSeleccionado(idCobro);
        if (!turno?.idTurno || idCobro === turno?.idCobro) return;
        vincularCobroTurno(turno.idTurno, idCobro)
            .then((actualizado) => onTurnoActualizado?.(actualizado))
            .catch((e) => onError(
                e?.response?.data?.mensaje
                || 'Se muestra el cobro elegido, pero no se pudo dejarlo registrado en el turno.'
            ));
    }, [turno?.idTurno, turno?.idCobro, onTurnoActualizado, onError]);

    // En la etapa 1 todavía se están confirmando los datos del turno: nadie mira los cobros.
    useEffect(() => {
        if (esEtapaUno) return;
        cargarLista();
    }, [esEtapaUno, cargarLista]);

    useEffect(() => {
        cargarDetalle();
    }, [cargarDetalle]);

    // Si el turno ya venía vinculado, esa es la selección inicial. Solo se sincroniza cuando no
    // hay nada elegido: si el operador ya eligió a mano, su elección manda.
    useEffect(() => {
        if (turno?.idCobro && !idCobroSeleccionado) setIdCobroSeleccionado(turno.idCobro);
    }, [turno?.idCobro, idCobroSeleccionado]);

    // Con un solo cobro no hay nada que elegir: se abre directo.
    useEffect(() => {
        if (!idCobroSeleccionado && cobros.length === 1) elegirCobro(cobros[0].idCobro);
    }, [cobros, idCobroSeleccionado, elegirCobro]);

    // ModalNuevoCobro no devuelve el cobro que creó, así que se recarga la lista y se abre el que
    // apareció. Si no se puede distinguir, queda la lista para elegir a mano.
    const handleCobroCreado = async () => {
        const idsPrevios = new Set(cobros.map((c) => c.idCobro));
        const lista = await cargarLista();
        const nuevo = lista.find((c) => !idsPrevios.has(c.idCobro));
        if (nuevo) elegirCobro(nuevo.idCobro);
    };

    const handleFinalizar = async () => {
        setGuardando(true);
        onError('');
        try {
            // Sin validación de motivo de no avance: un turno de cobro no abre trámite, así que
            // la exigencia de handleFinalizarAtencion (el flujo de mostrador) no aplica acá.
            await cambiarEstado(turno.idTurno, 'FINALIZADO', {
                notasAtencion: notas,
                resultadoAtencion: resultadoAtencion || '',
            });
            onAtencionFinalizada();
        } catch (e) {
            onError(e?.response?.data?.mensaje || 'No se pudo finalizar la atención.');
        } finally {
            setGuardando(false);
        }
    };

    const cuotas = useMemo(() => cobroDetalle?.cuotas || [], [cobroDetalle]);
    // Interruptor "Pagó todo / Saldo en cuotas" (ver renderInterruptorTipo).
    const [guardandoTipo, setGuardandoTipo] = useState(false);
    const [errorTipo, setErrorTipo] = useState('');

    // ─────────────────────────── Etapa 1 ───────────────────────────

    if (esEtapaUno) {
        return (
            <div className="atn-shell">
                <div className="atn-card atn-header">
                    <div className="atn-header-top">
                        <div>
                            <h2 className="atn-title">Datos del turno</h2>
                            <p className="atn-subtitle">Verificá los datos del cliente antes de empezar el cobro.</p>
                        </div>
                        <span className="atn-badge-etapa">Etapa 1</span>
                    </div>
                </div>

                <div className="atn-contenido">
                    <div className="atn-card">
                        <div className="atn-field-grid">
                            <CampoDato etiqueta="Cliente" valor={apellidoNombre(turno.apellido, turno.nombre)} />
                            <CampoDato etiqueta="CUIL" valor={turno.cuil || turno.dni || 'Sin dato'} muted={!(turno.cuil || turno.dni)} />
                            <CampoDato etiqueta="Tipo de turno" valor={formatearTipoTramite(turno.tipoTramite)} />
                            <CampoDato etiqueta="Fecha y Hora" valor={`${formatearFecha(turno.fechaTurno)} - ${formatearHorario(turno.horario)}`} />
                            <CampoDato etiqueta="Teléfono" valor={turno.telefonoCliente || 'Sin dato'} muted={!turno.telefonoCliente} />
                        </div>

                        {!clienteAtencion && (
                            <Alert variant="warning" className="py-2 mt-3 mb-0">
                                Este turno todavía no está vinculado a un cliente. Vinculalo para poder ver sus cobros.
                            </Alert>
                        )}

                        <div className="atn-field-divider" />

                        <div className="atn-note-block" style={{ marginTop: 0 }}>
                            <h5 className="atn-section-title">Motivo de consulta</h5>
                            <p>{turno.motivo || 'Sin motivo cargado.'}</p>
                        </div>
                    </div>

                    <div className="atn-actions">
                        <Button className="atn-btn-primary" onClick={onEmpezarAtencion} disabled={guardando}>
                            {guardando
                                ? <Spinner animation="border" size="sm" className="me-2" />
                                : <i className="bi bi-play-fill me-2"></i>}
                            Empezar Atención
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // ─────────────────────────── Etapa 2 ───────────────────────────

    const vinculado = Boolean(turno.cuilCliente);
    const leyendaVinculo = LEYENDA_CRITERIO[turno.vinculoClienteCriterio] || 'vinculado';
    const nombreCabecera = vinculado
        ? apellidoNombre(turno.clienteApellido, turno.clienteNombre)
        : apellidoNombre(turno.apellido, turno.nombre);
    const iniciales = (nombreCabecera || '')
        .split(/[\s,]+/).filter(Boolean).slice(0, 2)
        .map((p) => p.charAt(0)).join('').toUpperCase() || '?';

    const renderTarjetasTramites = () => {
        const lista = tramitesCliente || [];
        if (lista.length === 0) {
            return <p className="atn-text-secondary" style={{ margin: 0 }}>El cliente no tiene trámites activos.</p>;
        }
        return (
            <div className="tramites-grid" role="list">
                {lista.map((t) => {
                    const nombreTipo = t?.tipoTramite?.nombre ?? t?.nombreTipoTramite ?? t?.tipoTramite ?? '';
                    const { clave, icono } = metaTramite(nombreTipo);
                    const estado = t?.nombreEstadoActual || t?.estadoActual || 'Sin estado';
                    const abrir = () => onAbrirTramite?.(rutaDetalleTramite(t));
                    return (
                        <div
                            key={t.idTramite}
                            role="listitem"
                            className={`tramite-card tramite-card--${clave}`}
                            tabIndex={0}
                            title={`${nombreTipo || 'Trámite'} · ${estado}`}
                            onClick={abrir}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrir(); }
                            }}
                        >
                            <div className="tramite-card-head">
                                <div className="tramite-card-icon"><i className={`bi ${icono}`} /></div>
                                <div className="tramite-card-body">
                                    <h5 className="tramite-card-title">{nombreTipo || 'Tipo desconocido'}</h5>
                                    {/* Chip del lenguaje de la atención, no el badge de color de la
                                        ficha del cliente: es lo que hace que la grilla no se sienta
                                        pegada de otra pantalla. */}
                                    <span className="atn-chip atn-chip--neutral">{estado}</span>
                                </div>
                                <i className="bi bi-arrow-right-short tramite-card-arrow" />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // Interruptor principal (22-oct-2026): primero si pagó todo o queda saldo en cuotas; recién con
    // cuotas tiene sentido el plan y los montos. Lo usa cualquiera que atienda.
    const definirTipo = async (tipoPago) => {
        if (!cobroDetalle || cobroDetalle.tipoPago === tipoPago) return;
        setGuardandoTipo(true);
        setErrorTipo('');
        try {
            await definirTipoPagoCobro(cobroDetalle.idCobro, tipoPago);
            await Promise.all([cargarDetalle(), cargarLista()]);
        } catch (e) {
            setErrorTipo(e?.response?.data?.mensaje || 'No se pudo guardar.');
        } finally {
            setGuardandoTipo(false);
        }
    };

    const renderInterruptorTipo = () => {
        const tipo = cobroDetalle?.tipoPago || null;
        const opcion = (valor, icono, titulo, ayuda) => (
            <button
                type="button"
                className={`atn-tab${tipo === valor ? ' atn-tab--activa' : ''}`}
                aria-pressed={tipo === valor}
                disabled={soloLectura || guardandoTipo || cobroDetalle.estado === 'BAJA'}
                onClick={() => definirTipo(valor)}
                title={ayuda}
            >
                <i className={`bi ${icono} me-1`}></i>{titulo}
            </button>
        );
        return (
            <div className="mb-3">
                <div className="atn-form-label mb-1">¿Cómo paga?</div>
                <div className="atn-tabs" role="group" aria-label="Cómo paga">
                    {opcion('UN_SOLO_PAGO', 'bi-check2-all', 'Pagó todo', 'Un solo pago: queda pagado, sin monto ni plan')}
                    {opcion('EN_CUOTAS', 'bi-calendar3', 'Saldo en cuotas', 'Queda saldo: se arma el plan de cuotas')}
                </div>
                {!tipo && !soloLectura && (
                    <div className="atn-form-hint">Elegí primero si pagó todo o si queda saldo en cuotas.</div>
                )}
                {errorTipo && <Alert variant="danger" className="py-2 mt-2 mb-0">{errorTipo}</Alert>}
            </div>
        );
    };

    const renderSelectorDeCobro = () => {
        if (cobros.length <= 1) return null;
        return (
            <>
                <p className="atn-form-hint">El cliente tiene {cobros.length} cobros. Elegí cuál está pagando.</p>
                <div className="atn-tabs">
                    {cobros.map((c) => {
                        const etiqueta = (c.tramites || []).map((t) => t.tipoTramiteNombre).filter(Boolean).join(', ')
                            || c.banco || `Cobro #${c.idCobro}`;
                        return (
                            <button
                                key={c.idCobro}
                                type="button"
                                className={`atn-tab${c.idCobro === idCobroSeleccionado ? ' atn-tab--activa' : ''}`}
                                onClick={() => elegirCobro(c.idCobro)}
                            >
                                {etiqueta}
                                <span className="atn-text-secondary">· {esPagoTodo(c) ? PAGO_TODO : formatoMoneda(c.saldo)}</span>
                            </button>
                        );
                    })}
                </div>
            </>
        );
    };

    const renderCobro = () => {
        if (cargandoLista && cobros.length === 0) {
            return <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>;
        }

        if (!cuil) {
            return (
                <Alert variant="warning" className="py-2 mb-0">
                    Este turno todavía no está vinculado a un cliente. Vinculalo desde el botón de la
                    cabecera para poder ver sus cobros.
                </Alert>
            );
        }

        if (cobros.length === 0) {
            return (
                <div className="text-center py-3">
                    <p className="atn-text-secondary">
                        {apellidoNombre(clienteAtencion?.apellido, clienteAtencion?.nombre) || 'El cliente'} todavía
                        no tiene cobros cargados.
                    </p>
                    {!soloLectura && (
                        <Button className="atn-btn-primary" onClick={() => setModalNuevoAbierto(true)}>
                            <i className="bi bi-plus-lg me-2"></i>
                            Iniciar un cobro
                        </Button>
                    )}
                </div>
            );
        }

        return (
            <>
                {renderSelectorDeCobro()}

                {/* El spinner es de cuando se carga el detalle de un cobro que todavía no se vio. Al guardar
                    algo se vuelve a pedir el mismo cobro y el detalle sigue a la vista, para que no parpadee. */}
                {cargandoDetalle && cobroDetalle?.idCobro !== idCobroSeleccionado ? (
                    <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
                ) : !cobroDetalle ? (
                    <Alert variant="danger" className="py-2 mb-0">No se pudo cargar el cobro.</Alert>
                ) : (
                    <>
                        <div className="d-flex align-items-center gap-3 flex-wrap mb-3">
                            <ChipEstadoCobro estado={cobroDetalle.estado} />
                            <span className="text-muted small">Banco: <strong>{cobroDetalle.banco || 'Sin dato'}</strong></span>
                        </div>

                        {/* Sin "Información del pacto": editar el pacto, registrar el alta y dar de baja el cobro
                            no se deciden en el mostrador mientras el cliente espera. Eso sigue estando en la
                            ficha del trámite y en la del cliente, detrás de 'cobranzas.editar'
                            (ver Roles.COBRANZAS_OPERATIVO). Además trae un link que se va de la atención. */}
                        {renderInterruptorTipo()}
                        {/* Sin elegir cómo paga no se muestra nada que pida montos o un plan. Con "Pagó todo",
                            DetalleCobranza ya muestra PAGO TODO y oculta plan y saldo. */}
                        {cobroDetalle.tipoPago && (
                            <DetalleCobranza cobro={cobroDetalle} onCambio={cargarDetalle} mostrarPacto={false} />
                        )}
                    </>
                )}
            </>
        );
    };

    return (
        <div className="atn-shell">
            {/* ── 1. Datos del cliente ─────────────────────────────── */}
            <div className="atn-card atn-header">
                <div className="atn-header-top">
                    <div>
                        <h2 className="atn-title">Atención de cobro</h2>
                        <p className="atn-subtitle">
                            {soloLectura
                                ? 'Resumen de la atención ya finalizada.'
                                : 'Registrá el pago que trae el cliente y cerrá la atención.'}
                        </p>
                    </div>
                    <span className={`atn-badge-etapa${soloLectura ? ' atn-badge-etapa--success' : ''}`}>
                        {soloLectura ? 'Finalizado' : 'Etapa 2'}
                    </span>
                </div>

                <div className="atn-header-client">
                    <div className="atn-avatar-circle">{iniciales}</div>
                    <div className="atn-header-body">
                        <div className="atn-header-name">{nombreCabecera}</div>

                        {/* Misma fila de metadatos que la ficha de mostrador: CUIL y, al lado, el
                            chip del vínculo. Acá no va el acceso a la ficha del cliente porque
                            esta pantalla no lo tenía. */}
                        <div className="atn-header-meta">
                            <span>
                                CUIL {turno.cuilCliente || turno.cuil || turno.dni || 'Sin dato'}
                                {vinculado && ` · ${leyendaVinculo}`}
                            </span>
                            {vinculado && <span className="atn-chip atn-chip--success">Cliente del estudio</span>}
                        </div>
                    </div>

                    {!soloLectura && (
                        <div className="atn-header-actions">
                            <Button
                                variant="outline-secondary"
                                className="atn-btn-secondary"
                                onClick={onAbrirVinculoCliente}
                                title={vinculado
                                    ? 'Quitar el vínculo entre este turno y la ficha del cliente'
                                    : 'Vincular este turno con la ficha del cliente del estudio'}
                            >
                                <i className={`bi ${vinculado ? 'bi-person-dash-fill' : 'bi-person-check-fill'} me-2`}></i>
                                {vinculado ? 'Desvincular' : 'Vincular cliente'}
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <div className="atn-contenido">
                <div className="atn-card">
                    <div className="atn-field-grid">
                        <CampoDato etiqueta="Tipo de turno" valor={formatearTipoTramite(turno.tipoTramite)} />
                        <CampoDato etiqueta="Fecha y Hora" valor={`${formatearFecha(turno.fechaTurno)} - ${formatearHorario(turno.horario)}`} />
                        <CampoDato etiqueta="Teléfono" valor={turno.telefonoCliente || 'Sin dato'} muted={!turno.telefonoCliente} />
                    </div>
                </div>

                {cuil && (
                    <div className="atn-card">
                        <HistorialTurnosCliente
                            cuil={cuil}
                            titulo="Visitas anteriores"
                            idTurnoActual={turno.idTurno}
                            compacto
                            ocultarTurnoActual
                        />
                    </div>
                )}

                <div className="atn-card">
                    <h5 className="atn-section-title">Trámites del cliente</h5>
                    {renderTarjetasTramites()}
                </div>

                {/* ── 2. Datos del plan de cobro ───────────────────── */}
                <div className="atn-card">
                    <h5 className="atn-section-title">Plan de cobro</h5>
                    {renderCobro()}
                </div>

                {/* ── 3. Atención ──────────────────────────────────── */}
                {!soloLectura && (
                    <div className="atn-card">
                        <h5 className="atn-section-title">Atención</h5>

                        {cobroDetalle?.tipoPago === 'EN_CUOTAS' && (
                        <div className="atn-actions" style={{ justifyContent: 'flex-start', paddingTop: 0 }}>
                            <Button
                                variant="outline-secondary"
                                className="atn-btn-secondary"
                                onClick={() => setModalPagoAbierto(true)}
                                disabled={!cobroDetalle}
                                title={cobroDetalle ? '' : 'Elegí primero el cobro que está pagando'}
                            >
                                <i className="bi bi-cash-coin me-2"></i>
                                Registrar un pago
                            </Button>
                        </div>
                        )}
                        {cobroDetalle?.tipoPago === 'EN_CUOTAS' && <div className="atn-field-divider" />}

                        <Form.Group>
                            <Form.Label className="atn-form-label">Notas de la atención</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                className="atn-textarea"
                                value={notas}
                                onChange={(e) => setNotas(e.target.value)}
                                placeholder="Observaciones de la atención..."
                            />
                        </Form.Group>
                    </div>
                )}

                {soloLectura ? (
                    turno.resultadoAtencion && (
                        <div className="atn-card">
                            <h5 className="atn-section-title">Resultado de la atención</h5>
                            <p style={{ margin: 0 }}><strong>{turno.resultadoAtencion}</strong></p>
                        </div>
                    )
                ) : (
                    <div className="atn-card">
                        <h5 className="atn-section-title" style={{ marginBottom: '4px' }}>Resultado de la atención</h5>
                        <p className="atn-form-hint">
                            Cómo terminó la atención. Las opciones se administran desde Datos Maestros.
                        </p>
                        {/* Selector cerrado: desde la atención no se crean resultados nuevos. */}
                        <SelectorCatalogoConAlta
                            value={resultadoAtencion}
                            onChange={setResultadoAtencion}
                            opciones={resultadosAtencion || []}
                            placeholder="Escribí para buscar un resultado"
                            permitirAlta={false}
                        />
                    </div>
                )}

                {soloLectura && turno.notasAtencion && (
                    <div className="atn-card">
                        <h5 className="atn-section-title">Notas registradas</h5>
                        <p style={{ margin: 0 }}>{turno.notasAtencion}</p>
                    </div>
                )}

                {/* ── 4. Cierre ────────────────────────────────────── */}
                {!soloLectura && (
                    <div className="atn-actions">
                        <Button className="atn-btn-primary" onClick={handleFinalizar} disabled={guardando}>
                            {guardando
                                ? <Spinner animation="border" size="sm" className="me-2" />
                                : <i className="bi bi-check2-circle me-2"></i>}
                            Finalizar Atención
                        </Button>
                    </div>
                )}
            </div>

            {/* El "Registrar un pago" del mostrador (tarjeta Atención): entrega inicial, pago a cuenta o
                cancelación anticipada. Pagar una cuota, el plan y la bitácora están en DetalleCobranza. */}
            <ModalRegistrarPago
                open={modalPagoAbierto}
                idCobro={idCobroSeleccionado}
                cuotas={cuotas.filter((c) => !c.anulada && !c.pagada)}
                onClose={() => setModalPagoAbierto(false)}
                onGuardado={cargarDetalle}
            />
            <ModalNuevoCobro
                open={modalNuevoAbierto}
                cuil={cuil}
                onClose={() => setModalNuevoAbierto(false)}
                onCreado={handleCobroCreado}
            />
        </div>
    );
}
